'use client';

import type { RealtyAttributeValue } from '@eliadmin/core/src/types/realty';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Check } from 'lucide-react';
import { useId, useState } from 'react';

type RealtyDetailAttributeListProps = {
  values: RealtyAttributeValue[];
  label: string;
  initialVisible?: number;
};

export const RealtyDetailAttributeList = ({
  values,
  label,
  initialVisible = 4,
}: RealtyDetailAttributeListProps) => {
  const [showAll, setShowAll] = useState(false);
  const listId = useId();
  const hiddenCount = Math.max(0, values.length - initialVisible);
  const toggleText = showAll ? 'Zobraziť menej' : `Zobraziť viac (+${hiddenCount})`;

  return (
    <div className="mt-5">
      <ul id={listId} className="grid gap-3 sm:grid-cols-2">
        {values.map((value, index) => (
          <li
            key={value.id}
            className={cn(
              'min-w-0 items-center gap-3 text-sm text-foreground sm:text-base',
              index >= initialVisible && !showAll ? 'hidden md:flex' : 'flex',
            )}
            data-attribute-value-id={value.id}
            data-attribute-priority={index < initialVisible ? 'primary' : 'secondary'}
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Check className="h-4 w-4" aria-hidden="true" />
            </span>
            <span className="break-words">{value.name}</span>
          </li>
        ))}
      </ul>
      {hiddenCount > 0 && (
        <button
          type="button"
          className="mt-3 inline-flex min-h-11 items-center rounded-md py-2 text-sm font-bold text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:hidden"
          aria-controls={listId}
          aria-expanded={showAll}
          aria-label={`${toggleText} – ${label}`}
          data-eli-part="attribute-list-toggle"
          onClick={() => setShowAll(value => !value)}
        >
          {toggleText}
        </button>
      )}
    </div>
  );
};
